import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import type { GridRenderCellParams } from '@mui/x-data-grid-pro';

export const TeamLogoCell: React.FC<{
  src: string;
  alt: string;
  testId: string;
}> = ({ src, alt, testId }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          color: 'text.disabled',
        }}
        role="img"
        aria-label={`${alt} (image unavailable)`}
      >
        <BrokenImageOutlinedIcon sx={{ fontSize: 24 }} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="eager"
      referrerPolicy="no-referrer"
      onError={() => setHasError(true)}
      data-testid={testId}
      sx={{
        display: 'block',
        width: 30,
        height: 30,
        objectFit: 'contain',
      }}
    />
  );
};

/**
 * Builds an accessible aria-label for a score cell value.
 * Score values come pre-formatted from mapEventsToRows (e.g. "2 : 1", "- : -").
 *
 * @param scoreValue - Formatted score string
 * @returns Human-readable aria-label string
 */
const buildScoreAriaLabel = (scoreValue: string): string => {
  if (!scoreValue || scoreValue.trim() === '- : -') {
    return 'score not yet available';
  }

  // Parse "home : away" — strip whitespace around the colon separator
  const parts = scoreValue.split(':').map((s) => s.trim());
  if (parts.length === 2 && parts[0] !== '-' && parts[1] !== '-') {
    return `${parts[0]} to ${parts[1]}`;
  }

  // Partial score (one side is "-") — keep raw value as fallback
  return scoreValue;
};

export const renderTeamLogoCell = (
  params: GridRenderCellParams,
  teamName: string
) => {
  return (
    <TeamLogoCell
      src={params.value as string}
      alt={`${teamName} logo`}
      testId={`team-logo-${params.row.id}`}
    />
  );
};

/**
 * Renders a score cell with an accessible aria-label.
 * Score value is already formatted by mapEventsToRows
 * (e.g. "2 : 1", "- : -", "2 : 2 (4 : 3)").
 *
 * @param params - MUI DataGrid cell render parameters
 * @returns React element wrapping the formatted score string
 */
export const renderScoreCell = (params: GridRenderCellParams) => {
  const scoreValue = params.value as string;

  return <span aria-label={buildScoreAriaLabel(scoreValue)}>{scoreValue}</span>;
};

/**
 * Renders a location cell as a pin icon beside the venue name, truncated with an ellipsis and shown in full on hover.
 * @param params - MUI DataGrid cell render parameters
 * @returns React element with the pin icon and venue name
 */
export const renderLocationCell = (params: GridRenderCellParams) => {
  const location = params.value as string;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      title={location}
      sx={{ width: '100%', overflow: 'hidden' }}
    >
      <LocationOnOutlinedIcon
        fontSize="small"
        sx={{ color: 'action.active', flexShrink: 0 }}
      />
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {location}
      </Box>
    </Stack>
  );
};

const AGE_GROUP_STAGE_CELL_SX = { height: '100%', lineHeight: 1.3 };

/**
 * Renders the V2 Age group/Stage cell: the age group on the first line and the
 * stage name on the second, the stage alone without an age group, and nothing
 * for a game without a known bracket.
 */
export const renderAgeGroupStageCell = (params: GridRenderCellParams) => {
  const { ageGroupName, stageName } = params.row;

  return (
    <Stack justifyContent="center" sx={AGE_GROUP_STAGE_CELL_SX}>
      {ageGroupName && <Box component="span">{ageGroupName}</Box>}
      {stageName && <Box component="span">{stageName}</Box>}
    </Stack>
  );
};
